import { useEffect, useRef } from 'react';
import { useHubStore } from '../stores/hubStore';

export function useHubUnreadPolling(
  jobId: string | undefined,
  intervalMs: number,
  refreshParticipant: (participantKey: string) => void
): void {
  const { fetchHubUnreadCounts, hubUnreadCountByParticipantKey } = useHubStore();
  const previousCountsRef = useRef<Record<string, number>>({});

  // Reset previous counts when the job changes.
  useEffect(() => {
    previousCountsRef.current = {};
  }, [jobId]);

  // Poll unread counts at the given interval.
  useEffect(() => {
    if (!jobId || intervalMs <= 0) {
      return;
    }

    let cancelled = false;

    const fetchOnce = async () => {
      try {
        await fetchHubUnreadCounts(jobId);
      } catch {
        // Errors are handled in the store.
      }
    };

    // Initial fetch.
    fetchOnce();

    const id = setInterval(() => {
      if (cancelled) return;
      fetchOnce();
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [jobId, intervalMs, fetchHubUnreadCounts]);

  // When counts change, refresh participants whose unread count increased.
  useEffect(() => {
    const previous = previousCountsRef.current;
    const current = hubUnreadCountByParticipantKey;

    for (const [key, value] of Object.entries(current)) {
      const previousValue = previous[key] ?? 0;
      if (value > previousValue) {
        refreshParticipant(key);
      }
    }

    previousCountsRef.current = current;
  }, [hubUnreadCountByParticipantKey, refreshParticipant]);
}
